import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  CallTreatment,
  Contact,
  ContactAddress,
  ContactKind,
  ContactVisibility,
  CustomFieldDef,
  EmailAddress,
  NewContact,
  PhoneNumber
} from '@personical/cc-shared-types';
import { CALL_TREATMENTS } from '@personical/cc-shared-types';
import { addressPayload, countryOptions } from '../address.js';
import type { ApiClient, DuplicateMatch } from '../api.js';

const TREATMENT_LABEL: Record<CallTreatment, string> = {
  normal: 'Normal',
  vip: 'VIP — highlight when calling',
  screen: 'Screen — warn before connecting',
  block: 'Block — auto-decline'
};

interface Props {
  initial?: NewContact;
  submitLabel: string;
  customFields?: CustomFieldDef[];
  /** Provide to enable live duplicate detection. */
  api?: ApiClient;
  /** When editing an existing contact, pass its id so it isn't matched against itself. */
  excludeId?: string;
  onSubmit(input: NewContact): Promise<void>;
  onCancel(): void;
}

const EMPTY: NewContact = {
  displayName: '',
  jobTitle: '',
  department: '',
  address: {},
  company: '',
  website: '',
  emails: [],
  phoneNumbers: [],
  notes: '',
  tags: [],
  callTreatment: 'normal',
  visibility: 'shared',
  kind: 'person',
  custom: {}
};

const HTML_INPUT_TYPE: Record<CustomFieldDef['type'], string> = {
  text: 'text',
  url: 'url',
  date: 'date',
  number: 'number'
};

export function ContactForm({
  initial,
  submitLabel,
  customFields = [],
  api,
  excludeId,
  onSubmit,
  onCancel
}: Props) {
  const [form, setForm] = useState<NewContact>({
    ...EMPTY,
    ...initial,
    emails: initial?.emails && initial.emails.length > 0
      ? initial.emails
      : [{ type: 'work', address: '' }],
    phoneNumbers: initial?.phoneNumbers && initial.phoneNumbers.length > 0
      ? initial.phoneNumbers
      : [{ type: 'mobile', number: '' }],
    department: initial?.department ?? '',
    address: { ...(initial?.address ?? {}) },
    custom: { ...EMPTY.custom, ...(initial?.custom ?? {}) }
  });
  const [tagsText, setTagsText] = useState((initial?.tags ?? []).join(', '));
  const [companyText, setCompanyText] = useState(initial?.company ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [companyOptions, setCompanyOptions] = useState<Contact[]>([]);
  const dupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!api) return;
    if ((form.kind ?? 'person') !== 'person') return;
    let cancelled = false;
    api
      .list({ kind: 'company', limit: 100 })
      .then((res) => {
        if (!cancelled) setCompanyOptions(res.items);
      })
      .catch(() => {
        if (!cancelled) setCompanyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, form.kind]);

  useEffect(() => {
    if (companyText) return;
    if (!form.companyId) return;
    const match = companyOptions.find((c) => c.id === form.companyId);
    if (match) setCompanyText(match.displayName);
  }, [companyOptions, form.companyId, companyText]);

  useEffect(() => {
    if (!api) return;
    if (dupTimer.current) clearTimeout(dupTimer.current);
    const phones = form.phoneNumbers.filter((p) => p.number.trim().length >= 5);
    const emails = form.emails.filter((e) => e.address.includes('@'));
    if (phones.length === 0 && emails.length === 0) {
      setDuplicates([]);
      return;
    }
    dupTimer.current = setTimeout(() => {
      api
        .checkDuplicates({
          phoneNumbers: phones.map((p) => ({ number: p.number })),
          emails: emails.map((e) => ({ address: e.address })),
          excludeId
        })
        .then(setDuplicates)
        .catch(() => setDuplicates([]));
    }, 600);
    return () => {
      if (dupTimer.current) clearTimeout(dupTimer.current);
    };
  }, [form.phoneNumbers, form.emails, api, excludeId]);

  function update<K extends keyof NewContact>(key: K, value: NewContact[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function updateAddress(patch: Partial<ContactAddress>) {
    setForm((f) => ({ ...f, address: { ...(f.address ?? {}), ...patch } }));
  }

  function updateEmail(idx: number, patch: Partial<EmailAddress>) {
    setForm((f) => ({
      ...f,
      emails: f.emails.map((e, i) => (i === idx ? { ...e, ...patch } : e))
    }));
  }

  function addEmail() {
    update('emails', [...form.emails, { type: 'work', address: '' }]);
  }

  function removeEmail(idx: number) {
    setForm((f) => {
      const next = f.emails.filter((_, i) => i !== idx);
      return {
        ...f,
        emails: next.length > 0 ? next : [{ type: 'work', address: '' }]
      };
    });
  }

  function updatePhone(idx: number, patch: Partial<PhoneNumber>) {
    setForm((f) => ({
      ...f,
      phoneNumbers: f.phoneNumbers.map((p, i) => (i === idx ? { ...p, ...patch } : p))
    }));
  }

  function addPhone() {
    update('phoneNumbers', [...form.phoneNumbers, { type: 'mobile', number: '' }]);
  }

  function removePhone(idx: number) {
    setForm((f) => {
      const next = f.phoneNumbers.filter((_, i) => i !== idx);
      return {
        ...f,
        phoneNumbers: next.length > 0 ? next : [{ type: 'mobile', number: '' }]
      };
    });
  }

  async function resolveCompany(
    text: string,
    visibility: ContactVisibility
  ): Promise<{ companyId?: string; companyName?: string }> {
    const trimmed = text.trim();
    if (!trimmed) return { companyId: undefined, companyName: undefined };
    const match = companyOptions.find(
      (c) => c.displayName.toLowerCase() === trimmed.toLowerCase()
    );
    if (match) return { companyId: match.id, companyName: match.displayName };
    if (!api) return { companyId: undefined, companyName: trimmed };
    const created = await api.create({
      ...EMPTY,
      displayName: trimmed,
      kind: 'company',
      visibility
    });
    setCompanyOptions((prev) => [...prev, created]);
    return { companyId: created.id, companyName: created.displayName };
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const tags = tagsText
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      // Drop empty custom values so the doc stays compact.
      const customClean: Record<string, string> = {};
      for (const f of customFields) {
        const v = (form.custom?.[f.id] ?? '').trim();
        if (v) customClean[f.id] = v;
        if (f.required && !v) {
          throw new Error(`${f.label} is required`);
        }
      }

      const kind: ContactKind = form.kind ?? 'person';
      const visibility = form.visibility ?? 'shared';
      const { companyId, companyName } =
        kind === 'person'
          ? await resolveCompany(companyText, visibility)
          : { companyId: undefined, companyName: form.company?.trim() || undefined };

      const payload: NewContact = {
        ...form,
        displayName: form.displayName.trim(),
        jobTitle: form.jobTitle?.trim() || undefined,
        // Sent as null when blank: the API keeps a department or address
        // that an update leaves out, so clearing one must say so.
        department: form.department?.trim() || null,
        address: addressPayload(form.address),
        company: companyName,
        website: normalizeWebsite(form.website ?? '') || undefined,
        notes: form.notes?.trim() || undefined,
        emails: form.emails.filter((e) => e.address.trim().length > 0),
        phoneNumbers: form.phoneNumbers.filter((p) => p.number.trim().length > 0),
        tags,
        callTreatment: form.callTreatment ?? 'normal',
        visibility,
        kind,
        companyId,
        custom: customClean
      };
      if (!payload.displayName) {
        throw new Error('Display name is required');
      }
      await onSubmit(payload);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-sm rounded p-3">{error}</div>
      )}

      {duplicates.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-200 text-sm rounded p-3 space-y-2">
          <p className="font-medium">
            Possibly already a contact ({duplicates.length} match
            {duplicates.length === 1 ? '' : 'es'})
          </p>
          <ul className="space-y-1">
            {duplicates.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/contacts/${d.id}`}
                  target="_blank"
                  rel="noopener"
                  className="underline font-medium"
                >
                  {d.displayName}
                </Link>
                <span className="text-xs">
                  matches by {d.reason} <span className="font-mono">{d.matchedValue}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Field label="Type">
        <div className="flex gap-2">
          {(['person', 'company'] as ContactKind[]).map((k) => {
            const isActive = (form.kind ?? 'person') === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    kind: k,
                    companyId: k === 'company' ? undefined : prev.companyId
                  }));
                }}
                aria-pressed={isActive}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? 'border-slate-900 dark:border-slate-100 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                    : 'border-input hover:surface-muted'
                }`}
              >
                {k === 'person' ? 'Person' : 'Company'}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label={(form.kind ?? 'person') === 'company' ? 'Company name' : 'Name'} required>
        <input
          type="text"
          required
          value={form.displayName}
          onChange={(e) => update('displayName', e.target.value)}
          className={inputCls}
        />
      </Field>

      {(form.kind ?? 'person') === 'person' && (
        <Field label="Works at (optional)">
          <input
            type="text"
            list="contact-form-companies"
            value={companyText}
            onChange={(e) => setCompanyText(e.target.value)}
            placeholder="Type or pick a company"
            autoComplete="off"
            className={inputCls}
          />
          <datalist id="contact-form-companies">
            {companyOptions.map((c) => (
              <option key={c.id} value={c.displayName} />
            ))}
          </datalist>
        </Field>
      )}

      {(form.kind ?? 'person') === 'person' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Job title">
            <input
              type="text"
              value={form.jobTitle ?? ''}
              onChange={(e) => update('jobTitle', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Department">
            <input
              type="text"
              value={form.department ?? ''}
              onChange={(e) => update('department', e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
      )}

      <Field label="Website">
        <input
          type="text"
          inputMode="url"
          value={form.website ?? ''}
          onChange={(e) => update('website', e.target.value)}
          onBlur={(e) => update('website', normalizeWebsite(e.target.value))}
          placeholder="example.com"
          className={inputCls}
        />
      </Field>

      <section className="space-y-2">
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-mid">Emails</h3>
          <button type="button" onClick={addEmail} className={ghostBtnCls}>
            + Add email
          </button>
        </header>
        {form.emails.map((email, idx) => (
          <div key={idx} className="flex gap-2 items-center">
            <select
              value={email.type}
              onChange={(e) => updateEmail(idx, { type: e.target.value as EmailAddress['type'] })}
              className={selectCls}
            >
              <option value="work">work</option>
              <option value="personal">personal</option>
              <option value="other">other</option>
            </select>
            <input
              type="email"
              placeholder="name@example.com"
              value={email.address}
              onChange={(e) => updateEmail(idx, { address: e.target.value })}
              className={`${inputCls} flex-1`}
            />
            <button type="button" onClick={() => removeEmail(idx)} className={iconBtnCls}>
              ×
            </button>
          </div>
        ))}
      </section>

      <section className="space-y-2">
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-mid">Phone numbers</h3>
          <button type="button" onClick={addPhone} className={ghostBtnCls}>
            + Add phone
          </button>
        </header>
        {form.phoneNumbers.map((phone, idx) => (
          <div key={idx} className="flex gap-2 items-center">
            <select
              value={phone.type}
              onChange={(e) => updatePhone(idx, { type: e.target.value as PhoneNumber['type'] })}
              className={selectCls}
            >
              <option value="mobile">mobile</option>
              <option value="work">work</option>
              <option value="home">home</option>
              <option value="other">other</option>
            </select>
            <input
              type="tel"
              placeholder="+44 ..."
              value={phone.number}
              onChange={(e) => updatePhone(idx, { number: e.target.value })}
              className={`${inputCls} flex-1`}
            />
            <button type="button" onClick={() => removePhone(idx)} className={iconBtnCls}>
              ×
            </button>
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-mid">Address</h3>
        <Field label="Street">
          <textarea
            rows={2}
            value={form.address?.street ?? ''}
            onChange={(e) => updateAddress({ street: e.target.value })}
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Town or city">
            <input
              type="text"
              autoComplete="off"
              value={form.address?.city ?? ''}
              onChange={(e) => updateAddress({ city: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="County, state or region">
            <input
              type="text"
              autoComplete="off"
              value={form.address?.region ?? ''}
              onChange={(e) => updateAddress({ region: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Postcode">
            <input
              type="text"
              autoComplete="off"
              value={form.address?.postalCode ?? ''}
              onChange={(e) => updateAddress({ postalCode: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Country">
            <select
              value={form.address?.country ?? ''}
              onChange={(e) => updateAddress({ country: e.target.value || undefined })}
              className={inputCls}
            >
              <option value="">—</option>
              {countryOptions().map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Call treatment">
          <select
            value={form.callTreatment ?? 'normal'}
            onChange={(e) => update('callTreatment', e.target.value as CallTreatment)}
            className={inputCls}
          >
            {CALL_TREATMENTS.map((t) => (
              <option key={t} value={t}>
                {TREATMENT_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Visibility">
          <select
            value={form.visibility ?? 'shared'}
            onChange={(e) => update('visibility', e.target.value as ContactVisibility)}
            className={inputCls}
          >
            <option value="shared">Shared — everyone in your tenant</option>
            <option value="private">Private — only you</option>
          </select>
        </Field>
      </div>

      <Field label="Tags (comma-separated)">
        <input
          type="text"
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
          placeholder="partner, prospect"
          className={inputCls}
        />
      </Field>

      <Field label="Notes">
        <textarea
          rows={4}
          value={form.notes ?? ''}
          onChange={(e) => update('notes', e.target.value)}
          className={inputCls}
        />
      </Field>

      {customFields.length > 0 && (
        <section className="space-y-3 pt-2 border-t border-divider">
          <h3 className="text-sm font-semibold text-mid">Additional fields</h3>
          {customFields.map((f) => (
            <Field key={f.id} label={f.label} required={f.required}>
              <input
                type={HTML_INPUT_TYPE[f.type]}
                value={form.custom?.[f.id] ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    custom: { ...(prev.custom ?? {}), [f.id]: e.target.value }
                  }))
                }
                placeholder={f.placeholder}
                required={f.required}
                className={inputCls}
              />
            </Field>
          ))}
        </section>
      )}

      <div className="sticky bottom-14 md:bottom-0 -mx-6 -mb-6 px-6 py-3 surface border-t border-divider rounded-b-2xl flex gap-2 z-10">
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="btn-secondary px-4 py-2 text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  children
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-mid">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls = 'input w-full';
const selectCls = 'select';
const ghostBtnCls = 'text-xs text-muted hover:text-strong';
const iconBtnCls =
  'rounded-md border border-input bg-white dark:bg-slate-900 px-2 py-1 text-muted hover:surface-muted text-lg leading-none';

// Accept loose website input (`example.com`, `www.foo.co.uk`) and add a
// scheme so the link is browsable. Leave anything that already has a
// scheme — or that contains whitespace — alone so we don't munge values
// the user is mid-typing.
function normalizeWebsite(input: string): string {
  const t = input.trim();
  if (!t) return '';
  if (/^[a-z][a-z0-9+\-.]*:/i.test(t)) return t;
  if (/\s/.test(t)) return t;
  if (!t.includes('.')) return t;
  return `https://${t}`;
}
